import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Printer, Send, Check, Circle, User, Phone, Mail, Camera, FileSignature, MessageSquare, Wrench } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/Avatar';
import { StatusBadge } from '../components/service-orders/StatusBadge';
import { mockServiceOrders, mockClients, mockVehicles, mockMechanics } from '../data/mockData';
import { formatCurrency, formatPlate, formatPhone, formatDateTime } from '../lib/utils';
import { OSStatus } from '../lib/types';
const STATUS_FLOW: OSStatus[] = ['aguardando', 'em_analise', 'aprovado', 'executando', 'finalizado', 'entregue'];
export function ServiceOrderDetail() {
  const {
    id
  } = useParams();
  const os = mockServiceOrders.find(o => o.id === id);
  if (!os) {
    return <div className="flex h-full items-center justify-center flex-col gap-4" data-id="element-827">
        <h2 className="text-2xl font-bold" data-id="element-828">OS não encontrada</h2>
        <Button asChild data-id="element-829">
          <Link to="/app/ordens" data-id="element-830">Voltar para Ordens</Link>
        </Button>
      </div>;
  }
  const client = mockClients.find(c => c.id === os.clientId);
  const vehicle = mockVehicles.find(v => v.id === os.vehicleId);
  const mechanic = mockMechanics.find(m => m.id === os.mechanicId);
  const currentStatusIndex = STATUS_FLOW.indexOf(os.status);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="h-full flex flex-col space-y-6 pb-8" data-id="element-831">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-832">
        <div className="flex items-center gap-4" data-id="element-833">
          <Button variant="ghost" size="icon" asChild className="rounded-full" data-id="element-834">
            <Link to="/app/ordens" data-id="element-835">
              <ArrowLeft className="h-5 w-5" data-id="element-836" />
            </Link>
          </Button>
          <div data-id="element-837">
            <div className="flex items-center gap-3" data-id="element-838">
              <h1 className="text-2xl font-heading font-bold tracking-tight font-mono" data-id="element-839">
                {os.number}
              </h1>
              <StatusBadge status={os.status} data-id="element-840" />
            </div>
            <p className="text-muted-foreground text-sm mt-1" data-id="element-841">
              Criada em {formatDateTime(os.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-842">
          <Button variant="outline" size="sm" className="gap-2 hidden sm:flex" data-id="element-843">
            <Printer className="h-4 w-4" data-id="element-844" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" className="gap-2 hidden sm:flex" data-id="element-845">
            <Send className="h-4 w-4" data-id="element-846" /> Enviar para cliente
          </Button>
          <Button size="sm" className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-id="element-847">
            Avançar Status
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-848">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-849">
          {/* Vehicle & Client Card */}
          <Card className="overflow-hidden" data-id="element-850">
            <div className="flex flex-col sm:flex-row" data-id="element-851">
              <div className="w-full sm:w-1/3 h-48 sm:h-auto bg-muted relative" data-id="element-852">
                <img src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&q=80" alt="Vehicle" className="absolute inset-0 w-full h-full object-cover" data-id="element-853" />
              </div>
              <div className="flex-1 p-6 flex flex-col justify-between" data-id="element-854">
                <div data-id="element-855">
                  <div className="flex items-start justify-between mb-2" data-id="element-856">
                    <div data-id="element-857">
                      <h2 className="text-2xl font-bold" data-id="element-858">
                        {vehicle?.brand} {vehicle?.model}
                      </h2>
                      <p className="text-muted-foreground" data-id="element-859">
                        {vehicle?.year} • {vehicle?.engine} • {vehicle?.fuel}
                      </p>
                    </div>
                    <Badge variant="secondary" className="font-mono text-base px-3 py-1" data-id="element-860">
                      {formatPlate(vehicle?.plate || '')}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm" data-id="element-861">
                    <div data-id="element-862">
                      <span className="text-muted-foreground block text-xs uppercase tracking-wider" data-id="element-863">
                        VIN / Chassi
                      </span>
                      <span className="font-mono" data-id="element-864">{vehicle?.vin}</span>
                    </div>
                    <div data-id="element-865">
                      <span className="text-muted-foreground block text-xs uppercase tracking-wider" data-id="element-866">
                        Quilometragem
                      </span>
                      <span data-id="element-867">{vehicle?.mileage.toLocaleString('pt-BR')} km</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-868">
                  <div className="flex items-center gap-2" data-id="element-869">
                    <User className="h-4 w-4 text-muted-foreground" data-id="element-870" />
                    <span className="font-medium" data-id="element-871">{client?.name}</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-872">
                    <Phone className="h-4 w-4 text-muted-foreground" data-id="element-873" />
                    <span className="text-sm" data-id="element-874">
                      {formatPhone(client?.phone || '')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-875">
                    <Mail className="h-4 w-4 text-muted-foreground" data-id="element-876" />
                    <span className="text-sm" data-id="element-877">{client?.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Status Timeline */}
          <Card data-id="element-878">
            <CardContent className="p-6" data-id="element-879">
              <div className="relative flex justify-between items-center" data-id="element-880">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-border z-0" data-id="element-881" />
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-primary z-0 transition-all duration-500" style={{
                width: `${currentStatusIndex / (STATUS_FLOW.length - 1) * 100}%`
              }} data-id="element-882" />

                {STATUS_FLOW.map((status, idx) => {
                const isCompleted = idx < currentStatusIndex;
                const isCurrent = idx === currentStatusIndex;
                return <div key={status} className="relative z-10 flex flex-col items-center gap-2 bg-card px-2" data-id="element-883">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : isCurrent ? 'bg-background border-primary text-primary' : 'bg-background border-border text-muted-foreground'}`} data-id="element-884">
                        {isCompleted ? <Check className="h-4 w-4" data-id="element-885" /> : <Circle className="h-2 w-2 fill-current" data-id="element-886" />}
                      </div>
                      <span className={`text-xs font-medium capitalize ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`} data-id="element-887">
                        {status.replace('_', ' ')}
                      </span>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="servicos" className="w-full" data-id="element-888">
            <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent" data-id="element-889">
              <TabsTrigger value="servicos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3" data-id="element-890">
                Serviços e Peças
              </TabsTrigger>
              <TabsTrigger value="checklist" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3" data-id="element-891">
                Checklist
              </TabsTrigger>
              <TabsTrigger value="fotos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3" data-id="element-892">
                Fotos
              </TabsTrigger>
              <TabsTrigger value="obs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3" data-id="element-893">
                Observações
              </TabsTrigger>
              <TabsTrigger value="assinatura" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3" data-id="element-894">
                Assinatura
              </TabsTrigger>
            </TabsList>

            <TabsContent value="servicos" className="mt-6" data-id="element-895">
              <Card data-id="element-896">
                <Table data-id="element-897">
                  <TableHeader data-id="element-898">
                    <TableRow data-id="element-899">
                      <TableHead data-id="element-900">Item</TableHead>
                      <TableHead data-id="element-901">Tipo</TableHead>
                      <TableHead className="text-right" data-id="element-902">Qtd</TableHead>
                      <TableHead className="text-right" data-id="element-903">Valor Un.</TableHead>
                      <TableHead className="text-right" data-id="element-904">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-id="element-905">
                    {os.items.length > 0 ? os.items.map(item => <TableRow key={item.id} data-id="element-906">
                          <TableCell className="font-medium" data-id="element-907">
                            {item.description}
                          </TableCell>
                          <TableCell data-id="element-908">
                            <Badge variant="outline" className={item.type === 'part' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'} data-id="element-909">
                              {item.type === 'part' ? 'Peça' : 'Serviço'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right" data-id="element-910">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-right" data-id="element-911">
                            {formatCurrency(item.unitPrice)}
                          </TableCell>
                          <TableCell className="text-right font-medium" data-id="element-912">
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </TableCell>
                        </TableRow>) : <TableRow data-id="element-913">
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-id="element-914">
                          Nenhum item adicionado ainda.
                        </TableCell>
                      </TableRow>}
                  </TableBody>
                </Table>
                <div className="p-4 border-t border-border flex justify-end" data-id="element-915">
                  <Button variant="outline" className="gap-2" data-id="element-916">
                    <Wrench className="h-4 w-4" data-id="element-917" /> Adicionar Item
                  </Button>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="checklist" className="mt-6" data-id="element-918">
              <Card data-id="element-919">
                <CardContent className="p-6" data-id="element-920">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-921">
                    {[{
                    name: 'Freios dianteiros',
                    status: 'ok'
                  }, {
                    name: 'Pastilhas',
                    status: 'atencao'
                  }, {
                    name: 'Pneus',
                    status: 'critico'
                  }, {
                    name: 'Suspensão',
                    status: 'ok'
                  }, {
                    name: 'Bateria',
                    status: 'ok'
                  }, {
                    name: 'Óleo do motor',
                    status: 'nao_verificado'
                  }].map((item, i) => <div key={i} className="flex items-center justify-between p-4 border rounded-xl bg-muted/30" data-id="element-922">
                        <span className="font-medium text-sm" data-id="element-923">{item.name}</span>
                        <Badge variant="outline" className={item.status === 'ok' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : item.status === 'atencao' ? 'bg-automotive-orange/10 text-automotive-orange border-automotive-orange/20' : item.status === 'critico' ? 'bg-destructive/10 text-destructive border-destructive/20' : 'bg-muted text-muted-foreground'} data-id="element-924">
                          {item.status === 'ok' ? 'OK' : item.status === 'atencao' ? 'Atenção' : item.status === 'critico' ? 'Crítico' : 'Pendente'}
                        </Badge>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="fotos" className="mt-6" data-id="element-925">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-926">
                <div className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer" data-id="element-927">
                  <Camera className="h-8 w-8 mb-2" data-id="element-928" />
                  <span className="text-sm font-medium" data-id="element-929">Adicionar Foto</span>
                </div>
                <img src="https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=400&q=80" alt="Detail 1" className="aspect-square object-cover rounded-xl border border-border" data-id="element-930" />
                <img src="https://images.unsplash.com/photo-1600705722908-bab1e61c0b4d?w=400&q=80" alt="Detail 2" className="aspect-square object-cover rounded-xl border border-border" data-id="element-931" />
              </div>
            </TabsContent>

            <TabsContent value="obs" className="mt-6" data-id="element-932">
              <Card data-id="element-933">
                <CardContent className="p-6 space-y-6" data-id="element-934">
                  <div data-id="element-935">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2 uppercase tracking-wider" data-id="element-936">
                      Observações do Cliente
                    </h3>
                    <p className="text-foreground bg-muted/30 p-4 rounded-xl border" data-id="element-937">
                      {os.notes || 'Nenhuma observação registrada.'}
                    </p>
                  </div>
                  <div className="space-y-4" data-id="element-938">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider" data-id="element-939">
                      Comentários Internos
                    </h3>
                    <div className="flex gap-4" data-id="element-940">
                      <Avatar className="h-8 w-8 mt-1" data-id="element-941">
                        <AvatarImage src={mechanic?.avatar} data-id="element-942" />
                        <AvatarFallback data-id="element-943">
                          {mechanic?.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-muted/30 p-4 rounded-xl border" data-id="element-944">
                        <div className="flex justify-between items-start mb-1" data-id="element-945">
                          <span className="font-medium text-sm" data-id="element-946">
                            {mechanic?.name}
                          </span>
                          <span className="text-xs text-muted-foreground" data-id="element-947">
                            Hoje, 10:30
                          </span>
                        </div>
                        <p className="text-sm" data-id="element-948">
                          Peças solicitadas ao fornecedor. Previsão de chegada
                          amanhã de manhã.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="assinatura" className="mt-6" data-id="element-949">
              <Card data-id="element-950">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center border-2 border-dashed border-border m-6 rounded-xl bg-muted/10" data-id="element-951">
                  <FileSignature className="h-12 w-12 text-muted-foreground mb-4 opacity-50" data-id="element-952" />
                  <h3 className="text-lg font-medium mb-1" data-id="element-953">
                    Cliente ainda não assinou
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6" data-id="element-954">
                    A assinatura digital pode ser coletada na entrega do
                    veículo.
                  </p>
                  <Button variant="outline" data-id="element-955">Coletar Assinatura Agora</Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Column (Sticky) */}
        <div className="space-y-6 lg:sticky lg:top-6" data-id="element-956">
          {/* Resumo Financeiro */}
          <Card data-id="element-957">
            <CardHeader data-id="element-958">
              <CardTitle data-id="element-959">Resumo Financeiro</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-960">
              <div className="flex justify-between text-sm" data-id="element-961">
                <span className="text-muted-foreground" data-id="element-962">Subtotal</span>
                <span data-id="element-963">{formatCurrency(os.total)}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-964">
                <span className="text-muted-foreground" data-id="element-965">Desconto</span>
                <span data-id="element-966">{formatCurrency(0)}</span>
              </div>
              <div className="pt-4 border-t border-border flex justify-between items-center" data-id="element-967">
                <span className="font-medium" data-id="element-968">Total</span>
                <span className="text-3xl font-bold text-primary" data-id="element-969">
                  {formatCurrency(os.total)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Mecânico */}
          <Card data-id="element-970">
            <CardHeader data-id="element-971">
              <CardTitle data-id="element-972">Responsável</CardTitle>
            </CardHeader>
            <CardContent data-id="element-973">
              {mechanic ? <div className="flex items-center gap-4" data-id="element-974">
                  <Avatar className="h-12 w-12" data-id="element-975">
                    <AvatarImage src={mechanic.avatar} data-id="element-976" />
                    <AvatarFallback data-id="element-977">{mechanic.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div data-id="element-978">
                    <p className="font-medium" data-id="element-979">{mechanic.name}</p>
                    <p className="text-sm text-muted-foreground" data-id="element-980">
                      {mechanic.specialty}
                    </p>
                  </div>
                </div> : <p className="text-sm text-muted-foreground" data-id="element-981">
                  Nenhum mecânico atribuído.
                </p>}
            </CardContent>
          </Card>

          {/* Atividade Recente */}
          <Card data-id="element-982">
            <CardHeader data-id="element-983">
              <CardTitle data-id="element-984">Atividade Recente</CardTitle>
            </CardHeader>
            <CardContent data-id="element-985">
              <div className="space-y-4 relative before:absolute before:inset-0 before:ml-2 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent" data-id="element-986">
                {[{
                title: 'Status alterado para Em análise',
                time: '10:02',
                icon: MessageSquare
              }, {
                title: 'Peça adicionada: Filtro de óleo',
                time: '09:45',
                icon: Wrench
              }, {
                title: 'OS Criada',
                time: '09:14',
                icon: Check
              }].map((act, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-987">
                    <div className="flex items-center justify-center w-5 h-5 rounded-full border border-border bg-background text-muted-foreground shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow z-10" data-id="element-988">
                      <act.icon className="h-2.5 w-2.5" data-id="element-989" />
                    </div>
                    <div className="w-[calc(100%-2rem)] md:w-[calc(50%-1.5rem)] p-3 rounded-lg border border-border bg-card shadow-sm" data-id="element-990">
                      <div className="flex items-center justify-between mb-1" data-id="element-991">
                        <span className="text-xs font-medium text-muted-foreground" data-id="element-992">
                          {act.time}
                        </span>
                      </div>
                      <p className="text-sm text-foreground" data-id="element-993">{act.title}</p>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </motion.div>;
}